import React, { useRef, useState } from "react";
import { Box, Button, Stack, TextField, Typography, Divider } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";

const MAX_BYTES = 1.5 * 1024 * 1024; // 1.5 MB

interface Props {
  /** Current image: a data: URI (uploaded) or an http(s) URL. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  size?: number;
}

/**
 * Reusable image picker: drag & drop or browse from the local system (stored as
 * a base64 data URI), OR paste an image URL. Shows a live preview. Used by both
 * the product and category editors (Sprint 1 QA #3/#12).
 */
const ImageInput: React.FC<Props> = ({ value, onChange, label = "Image", size = 96 }) => {
  const [err, setErr] = useState("");
  const [badPreview, setBadPreview] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const readFile = (f?: File | null) => {
    setErr("");
    if (!f) return;
    if (!f.type.startsWith("image/")) { setErr("Please choose an image (PNG, JPG, GIF, WebP)."); return; }
    if (f.size > MAX_BYTES) { setErr("Image must be under 1.5 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => { setBadPreview(false); onChange(String(reader.result)); };
    reader.onerror = () => setErr("Could not read that file.");
    reader.readAsDataURL(f);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files?.[0];
    readFile(f);
  };

  const hasImg = !!value && /^(data:|https?:|\/\/|\/)/.test(value.trim());
  const previewSrc = value.trim().startsWith("//") ? `https:${value.trim()}` : value.trim();

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 0.5 }} alignItems="flex-start">
        {/* Preview */}
        <Box sx={{ width: size, height: size, borderRadius: 1.5, border: "1px solid", borderColor: "divider",
          overflow: "hidden", display: "grid", placeItems: "center", bgcolor: "#f6f8f7", flexShrink: 0, color: "#b7c2bb" }}>
          {hasImg && !badPreview
            ? <Box component="img" src={previewSrc} alt="preview" onError={() => setBadPreview(true)}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <ImageNotSupportedIcon />}
        </Box>

        {/* Drop zone + browse */}
        <Box sx={{ flex: 1, width: "100%" }}>
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            sx={{
              p: 2, borderRadius: 1.5, textAlign: "center", cursor: "pointer",
              border: "1.5px dashed", borderColor: dragging ? "primary.main" : "divider",
              bgcolor: dragging ? "action.hover" : "transparent", transition: "all .15s",
            }}
          >
            <CloudUploadIcon color={dragging ? "primary" : "disabled"} />
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Drag & drop an image here, or <b>browse</b>
            </Typography>
            <Typography variant="caption" color="text.secondary">PNG / JPG / GIF / WebP · up to 1.5 MB</Typography>
            <input ref={fileRef} hidden type="file" accept="image/*"
              onChange={(e) => readFile(e.target.files?.[0])} />
          </Box>

          <Divider sx={{ my: 1.5 }}><Typography variant="caption" color="text.secondary">or</Typography></Divider>

          <TextField
            fullWidth size="small" label="Image URL"
            placeholder="https://example.com/image.png"
            value={value.trim().startsWith("data:") ? "" : value}
            onChange={(e) => { setBadPreview(false); onChange(e.target.value); }}
            InputProps={{ startAdornment: <LinkIcon fontSize="small" style={{ marginRight: 6, opacity: 0.6 }} /> }}
            helperText={value.trim().startsWith("data:") ? "An uploaded image is set. Type a URL to replace it." : " "}
          />

          <Stack direction="row" spacing={1}>
            {hasImg && <Button size="small" color="error" onClick={() => { onChange(""); setErr(""); setBadPreview(false); }}>Remove image</Button>}
          </Stack>
          {err && <Typography variant="caption" color="error">{err}</Typography>}
          {badPreview && hasImg && <Typography variant="caption" color="error">Could not load that image.</Typography>}
        </Box>
      </Stack>
    </Box>
  );
};

export default ImageInput;
